/**
 * Pure utilities for computing transaction indicator display values and the
 * shared resting-vs-energized rule. Used by QueryStatusBar and
 * TransactionToolbarControls, testable without React.
 */

import type { TransactionMode, TransactionState } from '../../core/types/workspaceTypes';

export type TransactionIndicatorInfo = {
  /** Display text, e.g. "Tx: Manual (Active)" */
  text: string;
  /** Tailwind color class for the indicator */
  colorClass: string;
  /** Tooltip text with full details */
  tooltip: string;
};

export type TransactionEnergyState = 'neutral' | 'energy';

const MODE_LABELS: Record<TransactionMode, string> = {
  auto: 'Auto',
  manual: 'Manual',
  smart: 'Smart',
};

/**
 * Whether a transaction indicator should carry the shared `--energy` "work
 * in flight / not yet committed" tint, or sit as a neutral ghost.
 *
 * Energized when:
 *  - the user has declared manual intent (mode === 'manual'), even before a
 *    transaction has actually begun, or
 *  - a transaction is genuinely open (state !== 'idle': 'active' or
 *    'error' — an errored transaction still needs a rollback decision, so
 *    it counts as open, not resting).
 *
 * Neutral otherwise — Auto mode always, and Smart mode before any DML has
 * opened a transaction (Smart behaves exactly like Auto until then).
 *
 * This is the "open-or-manual transaction dot" rule (CH-09 §6 item 4): the
 * always-amber `Tx: Auto` control was the flagship anti-pattern this rule
 * exists to fix. Orange means work in flight, never idle decoration.
 */
export function computeTransactionEnergyState(
  mode: TransactionMode,
  state: TransactionState,
): TransactionEnergyState {
  if (mode === 'auto') return 'neutral';
  if (mode === 'manual') return 'energy';
  // smart: behaves like auto until a transaction actually opens
  return state === 'idle' ? 'neutral' : 'energy';
}

/**
 * Compute transaction indicator text, color, and tooltip.
 */
export function computeTransactionIndicator(
  mode: TransactionMode,
  state: TransactionState,
): TransactionIndicatorInfo {
  if (mode === 'auto') {
    return {
      text: 'Tx: Auto',
      colorClass: 'text-muted-foreground',
      tooltip: 'Transaction mode: Auto-commit',
    };
  }

  const label = MODE_LABELS[mode];

  if (state === 'error') {
    // Error is its own destructive-red signal, not the energy family — it
    // needs to read as distinctly urgent, not just "in flight". Was a
    // hardcoded `text-red-500`; moved onto the shared `text-destructive`
    // token this task's cluster already consumes for the same fault-state
    // meaning at the tab dot and toolbar dot (G-4/T6.1b).
    return {
      text: `Tx: ${label} (Error)`,
      colorClass: 'text-destructive',
      tooltip: `Transaction mode: ${label} - Transaction error (rollback recommended)`,
    };
  }

  // Manual/Smart, idle or active: energized whenever open-or-manual is
  // true. --energy-text is the AA-rated text-scale variant (CH-09 §6
  // item 4) — this is small bold text, not a glyph/wash.
  const colorClass =
    computeTransactionEnergyState(mode, state) === 'energy'
      ? 'text-energy-text'
      : 'text-muted-foreground';

  if (state === 'active') {
    return {
      text: `Tx: ${label} (Active)`,
      colorClass,
      tooltip: `Transaction mode: ${label} - Transaction is active`,
    };
  }

  // idle
  return {
    text: `Tx: ${label}`,
    colorClass,
    tooltip: `Transaction mode: ${label} - No active transaction`,
  };
}
